// @flow
import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { autobind } from 'core-decorators';
import Headline from '../../Headline';

import LoadingIndicator from '../../LoadingIndicator';
import type BoardAccountStore from '../../../stores/BoardAccountStore';

import BookingTable from '../../BookingDetails/booking-table/BookingTable';
import InclusiveBookingTable from '../../BookingDetails/booking-table/InclusiveBookingTable';
import Button from '../../Button';
import isInAppView from '../../../utils/isInAppView';
import { Alert } from '../../redesign/Alert';
import ServiceItem from '../../../models/BoardAccount/ServiceItem';
import track from '../../../tracking';
import IconWarning from '../../redesign/icons/IconWarning';

import type MasterStore from '../../../stores/MasterStore';

type Props = {
  boardAccountStore: BoardAccountStore,
  masterStore: MasterStore,
};

@inject('boardAccountStore', 'masterStore')
@observer
export default class AdditionalServices extends Component<Props> {
  static breadcrumb = 'Zusatzleistungen';

  componentDidMount() {
    this.props.boardAccountStore.fetch();
    this.trackPageView();
  }

  trackPageView = () => {
    track.pageView(
      window.location.pathname,
      ['buchung-reiseunterlagen', 'zusatzleistungen'],
      'content'
    );
  };

  splitAdditionalBookings(additionalBookings) {
    const filterText = 'Bezahlung erfolgt über den Dienstleister';
    return additionalBookings.list.reduce(
      ([externalBookings, paidAdditionalBookings], item) => {
        if (item.name?.includes(filterText)) {
          externalBookings.push(item);
        } else {
          paidAdditionalBookings.push(item);
        }
        return [externalBookings, paidAdditionalBookings];
      },
      [[], []]
    );
  }

  @autobind
  handlePrint() {
    window.print();
  }

  render() {
    const { boardAccountStore, masterStore } = this.props;
    const { masterData } = masterStore;
    const { boardAccount } = boardAccountStore;

    if (!boardAccount) return null;

    const {
      additionalBookings, // addons
      bookingTableHeadline, // addons
      includedBookingTableHeadline, // addons
      includedBookings, // addons
    } = boardAccount;

    const [externalBookings, paidAdditionalBookings] =
      this.splitAdditionalBookings(additionalBookings);

    const externalBookingsHeadline =
      'Die hier reservierten Zusatzleistungen werden nicht über Ihr Bordkonto abrechnet. Bitte beachten Sie den Hinweis bei Ihrer reservierten Leistung.';

    return (
      <div className="page page-booking-details">
        <Alert
          headline="Hinweis für Leistungsbuchungen"
          className="mb-8"
          size="xs"
          icon={<IconWarning />}
        >
          Bitte beachten Sie, dass es bis zu 15 Minuten dauern kann, bis Ihre
          Leistungsbuchung in den Zusatzleistungen angezeigt wird. Der bei der
          Buchung angezeigte Preis bleibt unverändert, auch wenn er in den
          Zusatzleistungen möglicherweise abweicht.
        </Alert>

        <Headline className="l-mod" title="Zusatzleistungen" />

        {boardAccount ? (
          <div>
            <section className="l-mod  mt-8">
              {includedBookings.list.length >= 1 && (
                <div>
                  <p className="text-md">{includedBookingTableHeadline}</p>
                  <InclusiveBookingTable
                    hasVip1={masterData.someoneHasVipOption1}
                    hasVip2={masterData.someoneHasVipOption2}
                    items={includedBookings.list}
                  />
                </div>
              )}

              <p className="text-md">{bookingTableHeadline}</p>

              <BookingTable
                items={paidAdditionalBookings}
                totalText={additionalBookings.totalText}
                emptyText={
                  'Es wurden bisher keine Zusatzleistungen von Ihnen gebucht.'
                }
              />

              {additionalBookings.fineprint ? (
                <p>{additionalBookings.fineprint}</p>
              ) : null}

              <p className="text-md">{externalBookingsHeadline}</p>

              <BookingTable
                items={externalBookings}
                emptyText={
                  'Es wurden bisher keine Zusatzleistungen von Ihnen gebucht.'
                }
              />
            </section>

            {!isInAppView && (
              <div className="l-right l-mod">
                <Button dark big onClick={this.handlePrint}>
                  Drucken
                </Button>
              </div>
            )}
          </div>
        ) : (
          <LoadingIndicator />
        )}
      </div>
    );
  }
}
